/**
 * The shell's TanStack Query client (1.I-1 deliverable 1): no retry on a 4xx, answers fresh for
 * 30 s, and a 401 from any read ends the session the way the route guard does: the remembered
 * user is forgotten and the page goes to the login page with `next`.
 */
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '../auth/client.js';
import { loginRedirectUrl } from '../auth/require-session.js';
import { forgetMe, isSigningOut } from './session.js';

export const STALE_MS = 30_000;

export function isClientError(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

export function retryRead(failureCount: number, error: unknown): boolean {
  return !isClientError(error) && failureCount < 2;
}

export function createShellQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof ApiError && error.status === 401 && !isSigningOut()) {
          forgetMe();
          window.location.assign(loginRedirectUrl(window.location.pathname));
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: STALE_MS,
        retry: retryRead,
        refetchOnWindowFocus: false,
        // A sign-out empties the cache; nothing may read again with the session it just ended.
        enabled: () => !isSigningOut(),
      },
    },
  });
}

export const shellQueryClient = createShellQueryClient();
